import { useEffect, useState, useCallback, useRef } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Sparkles, Image as ImageIcon, Music, Film, Send, CheckCircle2, XCircle, Clock, Loader2, Rocket, ExternalLink } from "lucide-react";

const STAGE_META = {
  caption:        { label: "Caption AI (Claude)",      icon: Sparkles },
  image:          { label: "Image (Nano Banana)",      icon: ImageIcon },
  voice:          { label: "Voix (ElevenLabs Bella)",  icon: Music },
  video:          { label: "Vidéo Ken Burns",          icon: Film },
  post_facebook:  { label: "Publication Facebook",     icon: Send },
  post_instagram: { label: "Publication Instagram",    icon: Send },
  post_tiktok:    { label: "Publication TikTok",       icon: Send },
};

const IMAGE_STYLES = [
  { value: "dynamic_dojo_action", label: "Action dojo (énergique)" },
  { value: "dark_premium",        label: "Premium sombre (boutique)" },
  { value: "studio_clean",        label: "Studio clean (produit)" },
  { value: "kid_friendly",        label: "Petits Samouraïs (kids)" },
];

const VOICES = [
  { slug: "camille_fr_f",  label: "Bella (Camille FR/EN ♀)" },
  { slug: "promo_dynamic", label: "Rachel (énergique ♀)" },
  { slug: "ralph_alt_h",   label: "Josh (grave ♂)" },
];

const ASPECTS = [
  { value: "9:16", label: "9:16 (TikTok/Reels)" },
  { value: "1:1",  label: "1:1 (IG carré)" },
  { value: "16:9", label: "16:9 (FB landscape)" },
];

export default function StudioPipelineModule() {
  const [prompt, setPrompt] = useState("");
  const [targets, setTargets] = useState({ facebook: true, instagram: true, tiktok: false });
  const [style, setStyle] = useState("dynamic_dojo_action");
  const [voice, setVoice] = useState("camille_fr_f");
  const [aspect, setAspect] = useState("9:16");
  const [duration, setDuration] = useState(15);
  const [scheduleAt, setScheduleAt] = useState("");
  const [campaigns, setCampaigns] = useState([]);
  const [currentId, setCurrentId] = useState(null);
  const [currentDoc, setCurrentDoc] = useState(null);
  const [launching, setLaunching] = useState(false);
  const pollRef = useRef(null);

  const loadCampaigns = useCallback(async () => {
    try {
      const { data } = await api.get("/studio/campaigns?limit=15");
      setCampaigns(data.campaigns || []);
    } catch (e) { /* silent */ }
  }, []);

  useEffect(() => { loadCampaigns(); }, [loadCampaigns]);

  // Polling
  useEffect(() => {
    if (!currentId) return;
    const poll = async () => {
      try {
        const { data } = await api.get(`/studio/campaign/${currentId}`);
        setCurrentDoc(data);
        if (data.status === "completed" || data.status === "failed") {
          clearInterval(pollRef.current);
          loadCampaigns();
        }
      } catch (e) { /* silent */ }
    };
    poll();
    pollRef.current = setInterval(poll, 3000);
    return () => clearInterval(pollRef.current);
  }, [currentId, loadCampaigns]);

  const launch = async () => {
    if (!prompt.trim()) {
      toast.error("Décris d'abord ce que tu veux promouvoir");
      return;
    }
    const targetsList = Object.entries(targets).filter(([, v]) => v).map(([k]) => k);
    if (targetsList.length === 0) {
      toast.error("Sélectionne au moins 1 réseau");
      return;
    }
    setLaunching(true);
    try {
      const payload = {
        prompt, targets: targetsList, image_style: style,
        voice_slug: voice, aspect, duration_s: duration,
      };
      if (scheduleAt) payload.schedule_at = new Date(scheduleAt).toISOString();
      const { data } = await api.post("/studio/campaign", payload);
      toast.success("Campagne lancée — pipeline en cours");
      setCurrentId(data.campaign_id);
      setCurrentDoc({ id: data.campaign_id, status: "running", stages: {} });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lancement");
    }
    setLaunching(false);
  };

  const statusIcon = (s) => {
    if (s === "done") return <CheckCircle2 className="w-4 h-4 text-emerald-400" />;
    if (s === "failed") return <XCircle className="w-4 h-4 text-red-400" />;
    if (s === "running") return <Loader2 className="w-4 h-4 text-amber-400 animate-spin" />;
    if (s === "skipped" || s === "pending") return <Clock className="w-4 h-4 text-zinc-600" />;
    if (s === "scheduled") return <Clock className="w-4 h-4 text-blue-400" />;
    return <Clock className="w-4 h-4 text-zinc-600" />;
  };

  return (
    <div className="p-6 max-w-6xl mx-auto" data-testid="studio-pipeline-module">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-amber-300 flex items-center gap-2">
          <Rocket className="w-6 h-6" /> Studio AI Pipeline
        </h1>
        <p className="text-sm text-zinc-500 mt-1">
          1 idée → caption + image + voix + vidéo + cross-post FB/IG/TikTok automatique.
        </p>
      </div>

      {/* Form */}
      <div className="rounded-xl bg-zinc-900/60 border border-zinc-800 p-5 mb-6">
        <label className="text-xs uppercase tracking-wider text-zinc-400 block mb-1">Idée de campagne</label>
        <textarea
          value={prompt} onChange={(e) => setPrompt(e.target.value)}
          rows={3} placeholder="Ex: Cours d'essai (19,98 $) BJJ tout l'été 2026, sans engagement..."
          className="w-full px-3 py-2 rounded bg-zinc-950 border border-zinc-700 text-sm text-zinc-100 placeholder-zinc-600"
          data-testid="prompt-input"
        />

        <div className="grid md:grid-cols-3 gap-3 mt-4">
          <div>
            <label className="text-xs text-zinc-400 block mb-1">Style image</label>
            <select value={style} onChange={(e) => setStyle(e.target.value)}
              className="w-full px-3 py-2 rounded bg-zinc-950 border border-zinc-700 text-sm text-zinc-100"
              data-testid="style-select">
              {IMAGE_STYLES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs text-zinc-400 block mb-1">Voix</label>
            <select value={voice} onChange={(e) => setVoice(e.target.value)}
              className="w-full px-3 py-2 rounded bg-zinc-950 border border-zinc-700 text-sm text-zinc-100"
              data-testid="voice-select">
              {VOICES.map(v => <option key={v.slug} value={v.slug}>{v.label}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs text-zinc-400 block mb-1">Format vidéo</label>
            <select value={aspect} onChange={(e) => setAspect(e.target.value)}
              className="w-full px-3 py-2 rounded bg-zinc-950 border border-zinc-700 text-sm text-zinc-100"
              data-testid="aspect-select">
              {ASPECTS.map(a => <option key={a.value} value={a.value}>{a.label}</option>)}
            </select>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-3 mt-3">
          <div>
            <label className="text-xs text-zinc-400 block mb-1">Durée vidéo : {duration}s</label>
            <input type="range" min={5} max={45} step={1} value={duration}
              onChange={(e) => setDuration(+e.target.value)}
              className="w-full" data-testid="duration-slider" />
          </div>
          <div>
            <label className="text-xs text-zinc-400 block mb-1">Programmer (vide = immédiat)</label>
            <input type="datetime-local" value={scheduleAt} onChange={(e) => setScheduleAt(e.target.value)}
              className="w-full px-3 py-2 rounded bg-zinc-950 border border-zinc-700 text-sm text-zinc-100"
              data-testid="schedule-input" />
          </div>
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-3">
          <span className="text-xs text-zinc-400 uppercase">Réseaux :</span>
          {Object.entries(targets).map(([k, v]) => (
            <label key={k} className="flex items-center gap-1.5 text-sm cursor-pointer" data-testid={`target-${k}`}>
              <input type="checkbox" checked={v} onChange={(e) => setTargets({...targets, [k]: e.target.checked})} />
              <span className="capitalize text-zinc-300">{k}</span>
            </label>
          ))}
          <button onClick={launch} disabled={launching}
            className="ml-auto px-5 py-2 rounded bg-gradient-to-r from-amber-600 to-amber-500 hover:from-amber-500 hover:to-amber-400 text-zinc-950 font-bold text-sm flex items-center gap-2 disabled:opacity-50"
            data-testid="launch-btn">
            {launching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Rocket className="w-4 h-4" />}
            Lancer la campagne
          </button>
        </div>
      </div>

      {/* Current campaign status */}
      {currentDoc && (
        <div className="rounded-xl bg-zinc-900/60 border border-amber-700/40 p-5 mb-6" data-testid="current-campaign">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-semibold text-amber-300">🚀 Campagne en cours : {currentDoc.status}</h2>
            <code className="text-[10px] text-zinc-500">{currentDoc.id}</code>
          </div>
          <div className="space-y-1.5">
            {Object.entries(currentDoc.stages || {}).map(([k, info]) => {
              const meta = STAGE_META[k] || { label: k, icon: Clock };
              const I = meta.icon;
              return (
                <div key={k} className="flex items-center gap-3 px-3 py-1.5 rounded bg-zinc-950/50" data-testid={`stage-${k}`}>
                  <I className="w-4 h-4 text-zinc-500" />
                  <span className="text-xs text-zinc-300 flex-1">{meta.label}</span>
                  {statusIcon(info.status)}
                  {info.url && (
                    <a href={info.url} target="_blank" rel="noreferrer" className="text-amber-400 hover:text-amber-300">
                      <ExternalLink className="w-3.5 h-3.5" />
                    </a>
                  )}
                  {info.error && <span className="text-[10px] text-red-400 max-w-[300px] truncate">{info.error}</span>}
                  {info.reason && <span className="text-[10px] text-zinc-500 max-w-[300px] truncate">{info.reason}</span>}
                </div>
              );
            })}
          </div>
          {currentDoc.assets && (
            <div className="mt-4 grid grid-cols-2 md:grid-cols-3 gap-3 pt-3 border-t border-zinc-800">
              {currentDoc.assets.image_url && (
                <a href={currentDoc.assets.image_url} target="_blank" rel="noreferrer" className="block">
                  <img src={currentDoc.assets.image_url} alt="" className="w-full h-32 object-cover rounded border border-zinc-700" />
                  <div className="text-xs text-zinc-400 mt-1">🖼️ Image générée</div>
                </a>
              )}
              {currentDoc.assets.voice_url && (
                <div>
                  <audio src={currentDoc.assets.voice_url} controls className="w-full" />
                  <div className="text-xs text-zinc-400 mt-1">🎵 Voix MP3</div>
                </div>
              )}
              {currentDoc.assets.video_url && (
                <div>
                  <video src={currentDoc.assets.video_url} controls className="w-full h-32 object-cover rounded" />
                  <div className="text-xs text-zinc-400 mt-1">🎬 Vidéo {currentDoc.aspect}</div>
                </div>
              )}
            </div>
          )}
          {currentDoc.assets?.caption_fr && (
            <div className="mt-3 p-3 bg-zinc-950/50 rounded text-xs text-zinc-300">
              <div className="text-amber-400 mb-1">Caption FR :</div>
              <p className="whitespace-pre-wrap">{currentDoc.assets.caption_fr}</p>
              {currentDoc.assets.hashtags?.length > 0 && (
                <p className="text-amber-300 mt-1">{currentDoc.assets.hashtags.join(" ")}</p>
              )}
            </div>
          )}
        </div>
      )}

      {/* History */}
      <div className="rounded-xl bg-zinc-900/60 border border-zinc-800 overflow-hidden">
        <div className="px-4 py-3 border-b border-zinc-800">
          <h2 className="text-sm font-semibold text-zinc-200">📜 Historique campagnes ({campaigns.length})</h2>
        </div>
        {campaigns.length === 0 && <div className="p-8 text-center text-zinc-500 text-sm">Aucune campagne.</div>}
        {campaigns.map((c) => (
          <div key={c.id} onClick={() => setCurrentId(c.id)}
            className="px-4 py-3 border-t border-zinc-800 hover:bg-zinc-900/70 cursor-pointer flex items-center gap-3"
            data-testid={`campaign-${c.id}`}>
            <div className="shrink-0">{statusIcon(c.status === "completed" ? "done" : c.status)}</div>
            <div className="flex-1 min-w-0">
              <p className="text-sm text-zinc-300 truncate">{c.prompt}</p>
              <p className="text-xs text-zinc-500">{c.targets?.join(", ")} · {new Date(c.created_at).toLocaleString("fr-CA")}</p>
            </div>
            <span className="text-[10px] uppercase text-zinc-500">{c.status}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
